// firestore/createUserDoc.js

import { browser } from '$app/environment';
import { firestoreDb } from '$lib/firebase';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    setDoc,
    addDoc
} from 'firebase/firestore';
import { logError } from '$lib/helpers/errors';
import { showToast } from '$lib/helpers';

/**
 * Creates a user document after ensuring no duplicate email exists.
 * @param {string} uid - The user's UID. Optional for anonymous users.
 * @param {Object} data - The user's data.
 * @returns {boolean} - Returns true if the user document was created, false otherwise.
 */
export async function createUserDoc(uid, data) {
    if (!browser || !firestoreDb) {
        console.warn('createUserDoc called outside of browser or without Firestore DB instance.');
        return false;
    }

    try {
        const firestore = await firestoreDb();

        if (!data.email) {
            console.log('No email provided. Cannot create new user.');
            return false;
        }

        const formattedEmail = data.email.toLowerCase(); // Normalize email to lowercase

        // Reference to the 'users' collection
        const usersRef = collection(firestore, 'users');

        // Query to check if a user with the provided email already exists
        const emailQuery = query(
            usersRef,
            where('email', '==', formattedEmail)
        );

        const emailSnapshot = await getDocs(emailQuery);

        if (!emailSnapshot.empty) {
            const existingUser = emailSnapshot.docs[0].data();
            console.log(`Duplicate email found: ${formattedEmail}. User UID: ${existingUser.uid || 'N/A'}`);
            // showToast('Error', 'This email is already associated with an account.');
            return false;
        }

        // Prepare the user data object
        const userData = {
            accepTerms: true,
            address: data.address || null,
            city: data.city || null, // Corrected: Previously set to data.address
            country: 'United Kingdom',
            county: data.county || null,
            createdAt: Date.now(),
            discounts: [],
            email: formattedEmail,
            firstName: data.firstName || null,
            lastName: data.lastName || null,
            phone: data.phone || null,
            postCode: data.postCode || null,
            roles: ['CUSTOMER']
        };

        if (uid) {
            // If UID is provided, use it as the document ID
            const userDocRef = doc(firestore, 'users', uid);
            await setDoc(userDocRef, userData, { merge: true });
            console.log(`User document created/updated for UID: ${uid} and Email: ${formattedEmail}`);
        } else {
            // If no UID is provided, create a document with an auto-generated ID
            const newUserDocRef = await addDoc(usersRef, userData);
            console.log(`User document created with ID: ${newUserDocRef.id} and Email: ${formattedEmail}`);
        }

        return true;
    } catch (error) {
        console.log('createUserDoc error:', error);
        logError(error, 'firestore:createUserDoc');

        if (error.code === 'already-exists') {
            // showToast('Error', 'This email is already associated with an account.');
        } else {
            // showToast('Error', 'An error occurred while creating your account. Please try again.');
        }

        return false;
    }
}
